import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, Edit2, Award, Trophy, Star,
  CheckCircle, Loader2, AlertCircle, Users, FileText,
  Settings, Eye, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminRecognitions() {
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    required_constancias: 5,
    recognition_title: '',
    badge_color: '#d4af37',
    is_active: true
  });

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [programsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/recognition/programs`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/recognition/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPrograms(programsRes.data.programs || []);
      setStats(statsRes.data);
    } catch (err) {
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      required_constancias: 5,
      recognition_title: '',
      badge_color: '#d4af37',
      is_active: true
    });
    setEditingProgram(null);
  };

  const handleEdit = (program) => {
    setFormData({
      name: program.name,
      description: program.description || '',
      required_constancias: program.required_constancias,
      recognition_title: program.recognition_title || '',
      badge_color: program.badge_color || '#d4af37',
      is_active: program.is_active
    });
    setEditingProgram(program);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('El nombre es requerido');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (editingProgram) {
        await axios.put(
          `${API_URL}/api/recognition/programs/${editingProgram.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Programa actualizado');
      } else {
        await axios.post(
          `${API_URL}/api/recognition/programs/create`,
          {
            ...formData,
            recognition_title: formData.recognition_title || formData.name
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Programa creado');
      }

      setTimeout(() => setSuccess(''), 3000);
      resetForm();
      setShowForm(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error guardando programa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm('¿Estás seguro de eliminar este programa?')) return;

    try {
      await axios.delete(`${API_URL}/api/recognition/programs/${programId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Programa eliminado');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      setError('Error eliminando programa');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Sistema de Reconocimientos</h1>
            <p className="text-gray-600 mt-2">
              Gestiona programas de constancias y reconocimientos para socios UPIM
            </p>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Programa
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-600">×</button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_constancias}</p>
                  <p className="text-sm text-gray-500">Constancias emitidas</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Trophy className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_recognitions}</p>
                  <p className="text-sm text-gray-500">Reconocimientos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.active_programs}</p>
                  <p className="text-sm text-gray-500">Programas activos</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Star className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.values(stats.constancias_by_type || {}).reduce((a, b) => a + b, 0)}
                  </p>
                  <p className="text-sm text-gray-500">Por tipo de contenido</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                {editingProgram ? 'Editar Programa' : 'Nuevo Programa de Reconocimiento'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del programa *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ej: Especialista en Ventas"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    placeholder="Descripción del programa"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Constancias requeridas
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.required_constancias}
                      onChange={(e) => setFormData(prev => ({ ...prev, required_constancias: parseInt(e.target.value) || 1 }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color de insignia
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={formData.badge_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, badge_color: e.target.value }))}
                        className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.badge_color}
                        onChange={(e) => setFormData(prev => ({ ...prev, badge_color: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título del reconocimiento
                  </label>
                  <input
                    type="text"
                    value={formData.recognition_title}
                    onChange={(e) => setFormData(prev => ({ ...prev, recognition_title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Texto que aparece en el documento"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    Programa activo
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Programs List */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Programas de Reconocimiento</h2>
            <p className="text-sm text-gray-500">
              Los socios obtienen reconocimientos al completar el número requerido de constancias
            </p>
          </div>

          {programs.length === 0 ? (
            <div className="p-12 text-center">
              <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay programas</h3>
              <p className="text-gray-500 mb-4">Crea tu primer programa de reconocimiento</p>
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium"
              >
                Crear programa
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {programs.map(program => (
                <div key={program.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${program.badge_color}20` }}
                      >
                        <Trophy className="w-6 h-6" style={{ color: program.badge_color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{program.name}</h3>
                          {!program.is_active && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                              Inactivo
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{program.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <span className="text-sm text-gray-600">
                            <strong className="text-indigo-600">{program.required_constancias}</strong> constancias requeridas
                          </span>
                          <span className="text-sm text-gray-500">
                            Título: {program.recognition_title || program.name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(program)}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(program.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 mb-3">¿Cómo funcionan las constancias y reconocimientos?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold mb-2">1</div>
              <p className="text-sm text-gray-600">
                <strong>Constancias:</strong> Los socios reciben una constancia elegante al completar cada curso, taller, webinar, etc. con su nombre, número de afiliación y fecha.
              </p>
            </div>
            <div>
              <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-bold mb-2">2</div>
              <p className="text-sm text-gray-600">
                <strong>Acumulación:</strong> Las constancias se acumulan automáticamente. Cuando un socio alcanza el número requerido, califica para un reconocimiento.
              </p>
            </div>
            <div>
              <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center font-bold mb-2">3</div>
              <p className="text-sm text-gray-600">
                <strong>Reconocimiento:</strong> El sistema genera automáticamente un reconocimiento mayor con todos los cursos completados listados.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
